/**
 * VRControllerManager - manages WebXR motion controllers and hand tracking. Uses VRInputDevice
 * abstraction for cleaner code and handles both motion controllers and hand tracking.
 */

import { Delegate } from '../utils/Delegate.js';

import { VRInputDevice } from './VRInputDevice.js';

import type { World } from '../game/World.js';
import type * as THREE from 'three';

export interface VRControllerOptions {
  /** Enable ray visualization */
  rayVisualizationEnabled?: boolean;
  /** Maximum ray distance for visualization and interaction */
  maxRayDistance?: number;
}

export interface ControllerInfo {
  inputSource: XRInputSource;
  device: VRInputDevice;
}

export enum XRButton {
  /** Primary trigger - required for all xr-standard controllers */
  Trigger = 'Trigger',
  /** Primary squeeze button - optional */
  Squeeze = 'Squeeze',
  /** Primary touchpad - optional */
  Touchpad = 'Touchpad',
  /** Primary thumbstick - optional */
  Thumbstick = 'Thumbstick'
}

export enum XRAxis {
  TouchpadX = 'TouchpadX',
  TouchpadY = 'TouchpadY',
  ThumbstickX = 'ThumbstickX',
  ThumbstickY = 'ThumbstickY'
}


/**
 * XR Button indices according to the "xr-standard" mapping
 * https://www.w3.org/TR/webxr-gamepads-module/#xr-standard-gamepad-mapping
 */
// Internal index mappings (xr-standard gamepad mapping)
const XR_BUTTON_TO_INDEX: Record<XRButton, number> = {
  [XRButton.Trigger]: 0,
  [XRButton.Squeeze]: 1,
  [XRButton.Touchpad]: 2,
  [XRButton.Thumbstick]: 3
};

const INDEX_TO_XR_BUTTON: Record<number, XRButton> = {
  0: XRButton.Trigger,
  1: XRButton.Squeeze,
  2: XRButton.Touchpad,
  3: XRButton.Thumbstick
};

const XR_AXIS_TO_INDEX: Record<XRAxis, number> = {
  [XRAxis.TouchpadX]: 0,
  [XRAxis.TouchpadY]: 1,
  [XRAxis.ThumbstickX]: 2,
  [XRAxis.ThumbstickY]: 3
};

const INDEX_TO_XR_AXIS: Record<number, XRAxis> = {
  0: XRAxis.TouchpadX,
  1: XRAxis.TouchpadY,
  2: XRAxis.ThumbstickX,
  3: XRAxis.ThumbstickY
};

/**
 * Button state information for XR controllers
 */
export interface XRButtonState {
  pressed: boolean;
  touched: boolean;
  value: number;
}

/**
 * Event data for XR button events
 */
export interface XRButtonEvent {
  inputSource: XRInputSource;
  button: XRButton;
  buttonState: XRButtonState;
}

/**
 * Event data for XR axis events
 */
export interface XRAxisEvent {
  inputSource: XRInputSource;
  axis: XRAxis;
  axisValue: number;
  previousValue: number;
}

export interface XRSqueezeEvent {
  inputSource: XRInputSource;
  frame: XRFrame;
  success?: boolean;
}

export class VRControllerManager {
  private devices: VRInputDevice[] = []; // Array of 2 devices (index 0 = left, index 1 = right)
  private world: World;
  private renderer: THREE.WebGLRenderer | null = null;
  private options: Required<VRControllerOptions>;

  // Button state tracking
  private previousButtonStates = new Map<XRInputSource, Map<number, XRButtonState>>();
  // Axis state tracking for thumbsticks and touchpads
  private previousAxisStates = new Map<XRInputSource, Map<number, number>>();

  // Public helpers: indexes are internal; expose enum <-> index mapping only here
  public static getButtonIndex(button: XRButton): number { return XR_BUTTON_TO_INDEX[button]; }
  public static getAxisIndex(axis: XRAxis): number { return XR_AXIS_TO_INDEX[axis]; }


  /** Fired when a controller is added */
  public readonly onControllerAdded: Delegate<[ControllerInfo], void> = new Delegate();
  /** Fired when a controller is removed */
  public readonly onControllerRemoved: Delegate<[XRInputSource], void> = new Delegate();
  /** Fired when controller position/state updates */
  public readonly onControllerUpdated: Delegate<[XRInputSource, VRInputDevice], void> = new Delegate();

  /** Fired when an XR controller button is pressed */
  public readonly onButtonDown: Delegate<[XRButtonEvent], void> = new Delegate();
  /** Fired when an XR controller button is released */
  public readonly onButtonUp: Delegate<[XRButtonEvent], void> = new Delegate();
  /** Fired when an XR controller button value changes (for analog buttons) */
  public readonly onButtonValueChanged: Delegate<[XRButtonEvent], void> = new Delegate();
  /** Fired when an XR controller axis value changes (thumbsticks, touchpads) */
  public readonly onAxisChanged: Delegate<[XRAxisEvent], void> = new Delegate();

  /** Fired when a squeeze gesture starts (grab/pinch) */
  public readonly onSqueezeStart: Delegate<[XRSqueezeEvent], void> = new Delegate();
  /** Fired when a squeeze gesture ends successfully */
  public readonly onSqueeze: Delegate<[XRSqueezeEvent], void> = new Delegate();
  /** Fired when a squeeze gesture ends (success or failure) */
  public readonly onSqueezeEnd: Delegate<[XRSqueezeEvent], void> = new Delegate();

  /** Fired when a select action starts (trigger/tap) */
  public readonly onSelectStart: Delegate<[XRSqueezeEvent], void> = new Delegate();
  /** Fired when a select action completes successfully */
  public readonly onSelect: Delegate<[XRSqueezeEvent], void> = new Delegate();
  /** Fired when a select action ends (success or failure) */
  public readonly onSelectEnd: Delegate<[XRSqueezeEvent], void> = new Delegate();

  constructor(world: World, options: VRControllerOptions = {}) {
    this.world = world;
    this.options = {
      rayVisualizationEnabled: options.rayVisualizationEnabled ?? true,
      maxRayDistance: options.maxRayDistance ?? 10
    };
  }


  public getRenderer(): THREE.WebGLRenderer | null {
    return this.renderer;
  }

  /** Setup the controller manager with a WebXR renderer */
  public setupWithRenderer(renderer: THREE.WebGLRenderer): void {
    this.renderer = renderer;

    // Create VR input devices for both controller indices
    this.createVRDevices();

    renderer.xr.addEventListener('sessionstart', () => {
      const session = renderer.xr.getSession();
      if (session) {
        // Add squeeze event listeners for hand tracking and controller gestures
        session.addEventListener('squeezestart', (event) => {
          const squeezeEvent: XRSqueezeEvent = {
            inputSource: event.inputSource,
            frame: event.frame
          };
          this.onSqueezeStart.invoke(squeezeEvent);
        });

        session.addEventListener('squeeze', (event) => {
          const squeezeEvent: XRSqueezeEvent = {
            inputSource: event.inputSource,
            frame: event.frame,
            success: true
          };
          this.onSqueeze.invoke(squeezeEvent);

          // Note: squeeze completion doesn't need additional InputManager forwarding
          // The press is already handled in squeezestart
        });

        session.addEventListener('squeezeend', (event) => {
          const squeezeEvent: XRSqueezeEvent = {
            inputSource: event.inputSource,
            frame: event.frame,
            success: false
          };
          this.onSqueezeEnd.invoke(squeezeEvent);
        });

        // Add select event listeners for primary actions (trigger/tap)
        session.addEventListener('selectstart', (event) => {
          const selectEvent: XRSqueezeEvent = {
            inputSource: event.inputSource,
            frame: event.frame
          };
          this.onSelectStart.invoke(selectEvent);
        });

        session.addEventListener('select', (event) => {
          const selectEvent: XRSqueezeEvent = {
            inputSource: event.inputSource,
            frame: event.frame,
            success: true
          };
          this.onSelect.invoke(selectEvent);

          // Note: select completion doesn't need additional InputManager forwarding
          // The press is already handled in selectstart
        });

        session.addEventListener('selectend', (event) => {
          const selectEvent: XRSqueezeEvent = {
            inputSource: event.inputSource,
            frame: event.frame,
            success: false
          };
          this.onSelectEnd.invoke(selectEvent);
        });
      }
    });

    renderer.xr.addEventListener('sessionend', () => {
      this.cleanup();
    });
  }

  private createVRDevices(): void {
    if (!this.renderer) {
      console.warn('[VRControllerManager] No renderer available for device creation');
      return;
    }

    // Create devices for both controller indices (0 and 1)
    for (let i = 0; i < 2; i++) {
      const device = new VRInputDevice(i, this.world, this.renderer, this.options);

      // Set up device event handlers
      device.onControllerConnected.add((inputSource) => {
        this.onControllerAdded.invoke({
          inputSource,
          device
        });
      });

      device.onControllerDisconnected.add((inputSource) => {
        this.onControllerRemoved.invoke(inputSource);
      });

      device.onControllerUpdated.add((inputSource) => {
        // Process button and axis events from XR gamepad
        this.processInputEvents(inputSource);

        // Fire update delegate
        this.onControllerUpdated.invoke(inputSource, device);
      });

      this.devices[i] = device;
    }
  }

  /** Update all controllers (call this every frame) */
  public updateControllers(): void {
    if (!this.renderer) return;

    const session = this.renderer.xr.getSession();
    if (!session) return;

    // Update all devices
    this.devices.forEach((device) => {
      device.update();
    });

    // Allow device handlers to run per-frame hooks (e.g., fingertip collider updates)
    this.devices.forEach((device: any) => {
      const handler = device.currentHandler;
      if (handler && typeof handler.update === 'function') {
        handler.update();
      }
    });
  }

  private getDeviceByHandedness(handedness: 'left' | 'right'): VRInputDevice | null {
    for (const device of this.devices) {
      if (device.getHandedness() === handedness) {
        return device;
      }
    }
    return null;
  }

  /** Get Three.js target ray group by handedness (for pointing/raycasting) */
  public getTargetRayGroup(handedness: 'left' | 'right'): THREE.Group | null {
    const device = this.getDeviceByHandedness(handedness);
    return device?.getTargetRayGroup() ?? null;
  }

  /** Get Three.js controller grip group by handedness */
  public getControllerGripGroup(handedness: 'left' | 'right'): THREE.Group | null {
    const device = this.getDeviceByHandedness(handedness);
    return device?.getGripGroup() ?? null;
  }

  /** Get target ray pose directly from input source (extracted from group) */
  public getTargetRayPose(inputSource: XRInputSource): { origin: THREE.Vector3; direction: THREE.Vector3 } | null {
    const device = this.getDeviceByInputSource(inputSource);
    return device?.getTargetRayPose() ?? null;
  }

  /** Get grip pose directly from input source (extracted from group) */
  public getGripPose(inputSource: XRInputSource): { position: THREE.Vector3; quaternion: THREE.Quaternion } | null {
    const device = this.getDeviceByInputSource(inputSource);
    return device?.getGripPose() ?? null;
  }

  private getDeviceByInputSource(inputSource: XRInputSource): VRInputDevice | null {
    for (const device of this.devices) {
      if (device.getInputSource() === inputSource) {
        return device;
      }
    }
    return null;
  }

  /** Get all active controllers */
  public getControllers(): ControllerInfo[] {
    const controllers: ControllerInfo[] = [];
    this.devices.forEach((device) => {
      const inputSource = device.getInputSource();
      if (inputSource) {
        controllers.push({
          inputSource,
          device
        });
      }
    });
    return controllers;
  }

  /** Get controller by handedness */
  public getControllerByHand(handedness: 'left' | 'right'): ControllerInfo | null {
    const device = this.getDeviceByHandedness(handedness);
    if (device) {
      const inputSource = device.getInputSource();
      if (inputSource) {
        return {
          inputSource,
          device
        };
      }
    }
    return null;
  }

  /** Cleanup all controllers */
  public cleanup(): void {
    this.devices.forEach((device) => {
      device.dispose();
    });
    this.devices = [];
    this.previousButtonStates.clear();
    this.previousAxisStates.clear();
  }

  private processInputEvents(inputSource: XRInputSource): void {
    // Only process if we have a gamepad
    if (!inputSource.gamepad) return;

    const gamepad = inputSource.gamepad;

    // Process button events
    this.processButtonChanges(inputSource, gamepad);

    // Process axis events (thumbsticks, touchpads)
    this.processAxisChanges(inputSource, gamepad);
  }

  private processButtonChanges(inputSource: XRInputSource, gamepad: Gamepad): void {
    let prevButtonStates = this.previousButtonStates.get(inputSource);

    // Initialize previous states if this is the first frame
    if (!prevButtonStates) {
      prevButtonStates = new Map<number, XRButtonState>();
      this.previousButtonStates.set(inputSource, prevButtonStates);
    }

    // Process each button according to the xr-standard mapping
    for (let i = 0; i < gamepad.buttons.length; i++) {
      const button = gamepad.buttons[i];
      const currentState: XRButtonState = {
        pressed: button.pressed,
        touched: button.touched,
        value: button.value
      };

      const prevState = prevButtonStates.get(i) ?? { pressed: false, touched: false, value: 0 };

      // Check for button down events
      if (currentState.pressed && !prevState.pressed) {
        const event = {
          inputSource,
          button: INDEX_TO_XR_BUTTON[i],
          buttonState: currentState
        };
        this.onButtonDown.invoke(event);
      }

      // Check for button up events
      if (!currentState.pressed && prevState.pressed) {
        const event = {
          inputSource,
          button: INDEX_TO_XR_BUTTON[i],
          buttonState: currentState
        };
        this.onButtonUp.invoke(event);
      }

      // Check for value changes (for analog buttons like triggers)
      if (Math.abs(currentState.value - prevState.value) > 0.001) {
        const event = {
          inputSource,
          button: INDEX_TO_XR_BUTTON[i],
          buttonState: currentState
        };
        this.onButtonValueChanged.invoke(event);
      }

      // Update previous state
      prevButtonStates.set(i, currentState);
    }
  }

  private processAxisChanges(inputSource: XRInputSource, gamepad: Gamepad): void {
    let prevAxisStates = this.previousAxisStates.get(inputSource);

    // Initialize previous states if this is the first frame
    if (!prevAxisStates) {
      prevAxisStates = new Map<number, number>();
      this.previousAxisStates.set(inputSource, prevAxisStates);
    }

    // Process each axis (thumbsticks and touchpads)
    for (let i = 0; i < gamepad.axes.length; i++) {
      const currentValue = gamepad.axes[i];
      const prevValue = prevAxisStates.get(i) ?? 0;

      // Check for significant axis changes (with deadzone)
      if (Math.abs(currentValue - prevValue) > 0.01) {
        const event = {
          inputSource,
          axis: INDEX_TO_XR_AXIS[i],
          axisValue: currentValue,
          previousValue: prevValue
        };
        this.onAxisChanged.invoke(event);

        // Update previous state
        prevAxisStates.set(i, currentValue);
      }
    }
  }

  /** Enable or disable ray visualization for all devices */
  public setRayVisualizationEnabled(enabled: boolean): void {
    this.options.rayVisualizationEnabled = enabled;
    this.devices.forEach((device) => {
      device.setRayVisualizationEnabled(enabled);
    });
  }

  /** Check if ray visualization is enabled */
  public isRayVisualizationEnabled(): boolean {
    return this.options.rayVisualizationEnabled;
  }

  /** Set controller model visibility by handedness (only affects motion controllers, not hands) */
  public setControllerVisible(handedness: 'left' | 'right', visible: boolean): void {
    const device = this.getDeviceByHandedness(handedness);
    if (device) {
      device.setControllerVisible(visible);
    }
  }
}